import { LuxLink } from "@lux/atoms/components/LuxLink";
import ChevronDownIcon from "@lux/icons/feather/chevron-down.svg";
import { getIn } from "@glow/shared";
import classNames from "classnames";
import range from "lodash/range";
import sortBy from "lodash/sortBy";
import React, { ReactNode, useMemo, useState } from "react";

type Accessor = string;

interface TableDataProps<Row> {
  columns: TableColumns<Row>;
  emptyState?: string;
  isLoading?: boolean;
  rows: Row[] | undefined;
  getHref?: (row: Row) => string;
  shimmerHeight?: number;
  loadingRows?: number;
}

export type TableColumns<Row> = Array<{
  headerName: string;
  alignRight?: boolean;
  hidden?: boolean;
  accessor?: Accessor;
  maxWidth?: number | string;
  width?: number | string;
  render: (row: Row, rowIdx: number) => ReactNode;
}>;

export const GlowTable = <Row,>({
  initialSortKey,
  initialSortAsc = true,
  rows,
  columns,
  ...props
}: TableDataProps<Row> & {
  initialSortKey?: string | null;
  initialSortAsc?: boolean;
}) => {
  const [sortInfo, setSortInfo] = useState<SortInfo | null>(
    initialSortKey
      ? {
          key: initialSortKey,
          direction: initialSortAsc ? "asc" : "desc",
        }
      : null,
  );

  const sortedRows = useMemo(() => {
    if (!rows) {
      return [];
    }

    if (!sortInfo) {
      return rows;
    }

    const _rows = sortBy(rows, (row) => {
      let val = getIn(row, sortInfo.key);

      if (typeof val === "string") {
        val = val.toLowerCase() as any;
      }

      return val;
    });

    if (sortInfo.direction === "desc") {
      _rows.reverse();
    }

    return _rows;
  }, [rows, sortInfo]);

  return (
    <TableControlled
      {...props}
      sortInfo={sortInfo}
      setSortInfo={setSortInfo}
      rows={sortedRows}
      columns={columns}
    />
  );
};

export type SortInfo = { key: string; direction: "asc" | "desc" };

const TableControlled = <Row,>({
  columns: allColumns,
  isLoading,
  shimmerHeight,
  loadingRows = 3,
  emptyState = "No rows found",
  rows,
  sortInfo,
  setSortInfo,
  isFixed,
  getHref,
}: TableDataProps<Row> & {
  sortInfo: SortInfo | null;
  setSortInfo: (sortInfo: SortInfo) => void;
  isFixed?: boolean;
}) => {
  const columns = allColumns.filter((column) => !column.hidden);

  return (
    <table className={classNames("zm-table", { fixed: isFixed })}>
      <thead>
        <tr>
          {columns.map(
            ({ headerName, accessor, alignRight, maxWidth, width }, idx) => {
              const isSorted = sortInfo && sortInfo.key === accessor;
              const sortable =
                accessor && !isLoading && (rows?.length ?? 0) > 0;

              return (
                <th key={idx} style={{ maxWidth, width }}>
                  <div
                    className={classNames("animated", {
                      sortable,
                      "align-right": alignRight,
                    })}
                    onClick={() => {
                      if (!accessor) {
                        return;
                      }

                      if (isSorted) {
                        setSortInfo({
                          key: sortInfo.key,
                          direction:
                            sortInfo.direction === "asc" ? "desc" : "asc",
                        });
                      } else {
                        setSortInfo({
                          key: accessor,
                          direction: "asc",
                        });
                      }
                    }}
                  >
                    {headerName}

                    {sortable && (
                      <ChevronDownIcon
                        className={classNames("animated", {
                          hide: !isSorted || isLoading,
                          asc: sortInfo?.direction === "asc",
                        })}
                      />
                    )}
                  </div>
                </th>
              );
            },
          )}
        </tr>
      </thead>
      {isLoading ? (
        <tbody className="shimmer-wrapper">
          {range(loadingRows).map((idx) => (
            <tr key={idx}>
              {columns.map(({ alignRight }, colIdx) => (
                <td
                  key={colIdx}
                  className={classNames("zm-table-cell", {
                    "align-right": alignRight,
                  })}
                >
                  <div style={{ height: shimmerHeight }}>
                    <div className="shimmer" />
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      ) : rows && rows.length > 0 ? (
        <tbody>
          {rows.map((row, rowIdx) => (
            <SimpleRow
              key={rowIdx}
              row={row}
              rowIdx={rowIdx}
              columns={columns}
              isLastRow={rowIdx === rows.length - 1}
              getHref={getHref}
            />
          ))}
        </tbody>
      ) : (
        <tbody>
          <tr>
            <td className="zm-table-cell" colSpan={columns.length}>
              <div className="empty-state">{emptyState}</div>
            </td>
          </tr>
        </tbody>
      )}

      <style jsx>{`
        table.zm-table {
          border-collapse: collapse;
          margin-bottom: 0;
          width: 100%;

          &.fixed {
            table-layout: fixed;
          }
        }

        .zm-table {
          th {
            font-size: var(--font-size-sm);
            white-space: nowrap;
            padding: 0.5rem 1rem;
            color: var(--table-header-color);
            font-weight: var(--font-weight-bold);
            border-bottom: 1px solid var(--table-header-divider-color);

            .align-right {
              display: flex;
              flex-direction: row-reverse;
              justify-content: flex-start;

              &.sortable :global(svg) {
                margin-left: 0;
                margin-right: 0.25rem;
              }
            }

            .sortable {
              display: flex;
              align-items: center;
              cursor: pointer;
              user-select: none;

              :global(svg) {
                color: var(--table-header-color);
                margin-left: 0.25rem;
                width: 1rem;
                height: 1rem;
                transform-origin: center;
              }

              :global(svg.asc) {
                transform: rotate(180deg);
              }
            }

            &.expand-th {
              width: 1.5rem;
              padding: 0;
            }

            :global(.hide) {
              opacity: 0;
            }

            .sortable:hover {
              color: var(--brand-color);

              :global(svg) {
                color: var(--brand-color);
                opacity: 1;
              }
            }
          }

          :global(td.zm-table-cell) {
            vertical-align: middle;
            padding: 0.5rem 1rem;
          }

          .shimmer {
            height: 0.75rem;
            margin-top: 0.375rem;
            margin-bottom: 0.375rem;
            width: 70%;
            max-width: 200px;
          }

          .empty-state {
            color: var(--table-empty-color);
            padding: 0.5rem 0;
          }
        }
      `}</style>
    </table>
  );
};

const SimpleRow = <Row,>({
  rowIdx,
  row,
  columns,
  isLastRow,
  getHref,
}: {
  rowIdx: number;
  row: Row;
  columns: Array<{
    headerName: string;
    alignRight?: boolean;
    accessor?: Accessor;
    maxWidth?: number | string;
    width?: number | string;
    render: (row: Row, rowIdx: number) => ReactNode;
  }>;
  isLastRow: boolean;
  getHref?: (row: Row) => string;
}) => {
  const href = getHref?.(row);
  return (
    <tr
      className={classNames({
        animated: true,
        clickable: true,
        last: isLastRow,
      })}
    >
      {columns.map(({ render, alignRight, maxWidth, width }, colIdx) => (
        <td className="zm-table-cell" key={colIdx} style={{ maxWidth, width }}>
          {href ? (
            <LuxLink href={href}>
              <div
                className={classNames("flex-1", { "align-right": alignRight })}
              >
                {render(row, rowIdx)}
              </div>
            </LuxLink>
          ) : (
            <div
              className={classNames("flex-1", { "align-right": alignRight })}
            >
              {render(row, rowIdx)}
            </div>
          )}
        </td>
      ))}

      <style jsx>{`
        tr :global(a) {
          display: block;
          color: inherit;
        }

        .clickable {
          cursor: pointer;

          &:hover {
            background-color: var(--table-hover-bg-color);
          }
        }

        tr {
          &.clickable:hover {
            background-color: var(--table-hover-bg-color);
          }

          &:not(.last) {
            border-bottom: 1px solid var(--table-divider-color);
          }
        }
      `}</style>
    </tr>
  );
};
